import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Chip, Divider, CircularProgress,
  Paper, Button, Avatar
} from '@mui/material';
import {
  Gavel as GavelIcon,
  AutoAwesome as AiIcon,
  Description as DocIcon,
  LocationOn as CityIcon,
  QuestionAnswer as QAIcon,
  ArrowForward as ArrowIcon,
  WorkspacePremium as WorkIcon,
  Search as SearchIcon,
  Stars as StarsIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface DocPubblico {
  id: string;
  nota: string;
  addedAt: string;
  document: {
    id: string;
    title: string;
    description: string | null;
    cities: string[];
    keywords: string[];
    uploadDate: string;
  };
}

interface AiChatPubblica {
  id: string;
  domanda: string;
  risposta: string;
  createdAt: string;
}

interface DatiFascicolo {
  nome: string;
  colore: string;
  createdAt: string;
  utente: string;
  documenti: DocPubblico[];
  aiChats: AiChatPubblica[];
  riepilogoAi: string | null;
  exportCreatedAt: string;
}

const mdSx = {
  '& p': { m: 0, mb: 1, fontSize: '0.9rem', lineHeight: 1.8, color: '#1e293b', '&:last-child': { mb: 0 } },
  '& strong': { fontWeight: 700, color: '#1B2A4A' },
  '& em': { fontStyle: 'italic', color: '#475569' },
  '& ul, & ol': { pl: 2.5, mb: 1 },
  '& li': { mb: 0.5, fontSize: '0.9rem', color: '#1e293b' },
  '& h1, & h2, & h3': { fontFamily: 'Syne, sans-serif', fontWeight: 700, color: '#1B2A4A', mt: 1.5, mb: 0.5 },
  '& hr': { borderColor: 'rgba(0,0,0,0.12)', my: 1.5 },
  '& blockquote': { borderLeft: '3px solid #7C3AED', pl: 1.5, ml: 0, color: '#475569', fontStyle: 'italic' },
};

const FascicoloPubblicoPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [dati, setDati] = useState<DatiFascicolo | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(false);

  useEffect(() => {
    const carica = async () => {
      try {
        const res = await axios.get(`/api/fascicoli/pubblico/${token}`);
        setDati(res.data.fascicolo);
      } catch {
        setErrore(true);
      } finally {
        setLoading(false);
      }
    };
    carica();
  }, [token]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#1B2A4A', mb: 2 }} />
        <Typography color="text.secondary">Caricamento fascicolo...</Typography>
      </Box>
    </Box>
  );

  if (errore || !dati) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
      <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
        <GavelIcon sx={{ fontSize: 56, color: 'rgba(0,0,0,0.2)', mb: 2 }} />
        <Typography variant="h6" fontWeight={700} gutterBottom>Link non trovato</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Questo link potrebbe essere stato rimosso o non è valido.
        </Typography>
        <Button variant="contained" href="https://itfplus.it" sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1B2A4A' }}>
          Vai su ITF Plus
        </Button>
      </Box>
    </Box>
  );

  const dataFormattata = new Date(dati.exportCreatedAt).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'long', year: 'numeric'
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>

      {/* ── HEADER BRANDING ─────────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1B2A4A 0%, #2d4070 50%, #1B2A4A 100%)',
        color: 'white', py: 3, px: 2,
        borderBottom: '3px solid #A67C52'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <GavelIcon sx={{ fontSize: 24, color: '#A67C52' }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                  ITF Plus
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                  Giurisprudenza italiana · Ricerca AI
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              href="https://itfplus.it"
              target="_blank"
              endIcon={<ArrowIcon />}
              sx={{
                color: 'white', borderColor: 'rgba(255,255,255,0.4)',
                textTransform: 'none', fontWeight: 600, borderRadius: 2,
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Accedi a ITF Plus
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* ── INTESTAZIONE FASCICOLO ──────────────────────────────────────────── */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <Box sx={{
              width: 8, minHeight: 60, borderRadius: 1, flexShrink: 0,
              bgcolor: dati.colore, mt: 0.5
            }} />
            <Box>
              <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: 2, fontSize: 11 }}>
                FASCICOLO LEGALE
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ fontFamily: '"Libre Baskerville", serif', lineHeight: 1.2, mb: 1 }}>
                {dati.nome}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Avatar sx={{ width: 22, height: 22, bgcolor: dati.colore, fontSize: 11, fontWeight: 800 }}>
                    {dati.utente?.[0]?.toUpperCase() || 'A'}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {dati.utente}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.disabled">·</Typography>
                <Typography variant="body2" color="text.disabled">
                  Generato il {dataFormattata}
                </Typography>
                <Typography variant="body2" color="text.disabled">·</Typography>
                <Chip
                  label={`${dati.documenti.length} ${dati.documenti.length === 1 ? 'sentenza' : 'sentenze'}`}
                  size="small" icon={<DocIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{ fontSize: 12, height: 22 }}
                />
                {dati.aiChats.length > 0 && (
                  <Chip
                    label={`${dati.aiChats.length} analisi AI`}
                    size="small" icon={<AiIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{ fontSize: 12, height: 22, bgcolor: 'rgba(124,58,237,0.08)', color: '#7C3AED' }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Nota esplicativa */}
          <Paper elevation={0} sx={{
            p: 2.5, borderRadius: 2,
            border: `1px solid ${dati.colore}30`,
            bgcolor: dati.colore + '06',
            display: 'flex', alignItems: 'flex-start', gap: 1.5
          }}>
            <WorkIcon sx={{ color: dati.colore, mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Questo documento è stato generato con <strong style={{ color: '#1B2A4A' }}>BOOKY Work</strong> — lo strumento di lavoro legale di ITF Plus.
              Raccoglie le sentenze selezionate dall'avvocato, i riepiloghi AI per ogni sentenza e le analisi del caso.
            </Typography>
          </Paper>
        </Box>

        {/* ── RIEPILOGO AI ────────────────────────────────────────────────────── */}
        {dati.riepilogoAi && (
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                background: 'linear-gradient(135deg, #7C3AED, #9333EA)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <AiIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800}>Riepiloghi AI delle sentenze</Typography>
                <Typography variant="caption" color="text.secondary">
                  Generato automaticamente dall'AI di ITF Plus · max 5 righe per sentenza
                </Typography>
              </Box>
            </Box>

            <Paper elevation={0} sx={{
              p: 3.5, borderRadius: 3,
              border: '1px solid rgba(124,58,237,0.15)',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.03) 0%, white 100%)',
              borderLeft: '4px solid #7C3AED'
            }}>
              <Box sx={mdSx}>
                <ReactMarkdown>{dati.riepilogoAi}</ReactMarkdown>
              </Box>
            </Paper>
          </Box>
        )}

        {/* ── SENTENZE DEL FASCICOLO ──────────────────────────────────────────── */}
        {dati.documenti.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2, bgcolor: '#1B2A4A',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <DocIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Sentenze raccolte ({dati.documenti.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Selezionate dall'avvocato con BOOKY Search
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dati.documenti.map((fd, i) => (
                <Paper key={fd.id} elevation={0} sx={{
                  p: 2.5, borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderLeft: `4px solid ${dati.colore}`,
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', transform: 'translateX(2px)' }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1, bgcolor: dati.colore + '18',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2
                    }}>
                      <Typography sx={{ color: dati.colore, fontSize: 12, fontWeight: 800 }}>{i + 1}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.4, mb: 1 }}>
                        {fd.document.title}
                      </Typography>

                      {fd.document.cities?.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {fd.document.cities.map(c => (
                            <Chip
                              key={c} label={c} size="small"
                              icon={<CityIcon sx={{ fontSize: '12px !important' }} />}
                              sx={{ fontSize: 11, height: 20, bgcolor: 'rgba(0,0,0,0.04)' }}
                            />
                          ))}
                        </Box>
                      )}

                      {fd.nota && (
                        <Box sx={{
                          bgcolor: '#FFF9EC', border: '1px solid rgba(166,124,82,0.2)',
                          borderRadius: 1.5, px: 1.5, py: 1, mt: 1
                        }}>
                          <Typography variant="caption" sx={{ color: '#92400e', display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                            <strong style={{ flexShrink: 0 }}>Nota:</strong> {fd.nota}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* ── CONVERSAZIONI AI ────────────────────────────────────────────────── */}
        {dati.aiChats.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                background: 'linear-gradient(135deg, #7C3AED, #9333EA)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <QAIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Analisi AI del caso ({dati.aiChats.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Domande e risposte dell'AI ragionate sulle sentenze del fascicolo
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {dati.aiChats.map((chat, i) => (
                <Paper key={chat.id} elevation={0} sx={{
                  borderRadius: 2, overflow: 'hidden',
                  border: '1px solid rgba(124,58,237,0.12)'
                }}>
                  {/* Domanda */}
                  <Box sx={{
                    px: 2.5, py: 2,
                    background: 'linear-gradient(135deg, #1B2A4A, #2d4070)',
                    display: 'flex', alignItems: 'flex-start', gap: 1.5
                  }}>
                    <Box sx={{
                      width: 24, height: 24, borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, mt: 0.2
                    }}>
                      <Typography sx={{ color: 'white', fontSize: 11, fontWeight: 800 }}>Q</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.5 }}>
                      {chat.domanda}
                    </Typography>
                  </Box>

                  {/* Risposta */}
                  <Box sx={{
                    px: 2.5, py: 2,
                    bgcolor: 'rgba(124,58,237,0.03)',
                    display: 'flex', alignItems: 'flex-start', gap: 1.5
                  }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1, flexShrink: 0, mt: 0.2,
                      background: 'linear-gradient(135deg, #7C3AED, #9333EA)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <AiIcon sx={{ color: 'white', fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={mdSx}>
                        <ReactMarkdown>{chat.risposta}</ReactMarkdown>
                      </Box>
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                        {new Date(chat.createdAt).toLocaleDateString('it-IT', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 5 }} />

        {/* ── FOOTER PROMOZIONALE ITF PLUS ───────────────────────────────────── */}
        <Box sx={{
          borderRadius: 4, overflow: 'hidden',
          background: 'linear-gradient(135deg, #1B2A4A 0%, #2d4070 60%, #1B2A4A 100%)',
          color: 'white', p: { xs: 3, md: 5 }
        }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: { md: 'center' } }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <GavelIcon sx={{ color: '#A67C52', fontSize: 28 }} />
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                  ITF Plus
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: 'rgba(255,255,255,0.95)' }}>
                La piattaforma di ricerca giurisprudenziale con AI per avvocati italiani
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, maxWidth: 500 }}>
                Questo fascicolo è stato creato con <strong style={{ color: 'white' }}>BOOKY Work</strong>, lo spazio di lavoro
                personale di ITF Plus. Ogni avvocato può raccogliere sentenze, annotarle,
                interrogare l'AI del caso e condividere i risultati in un click.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
              {[
                { icon: <SearchIcon sx={{ fontSize: 18 }} />, label: 'BOOKY Search', desc: 'Ricerca AI nelle sentenze' },
                { icon: <WorkIcon sx={{ fontSize: 18 }} />, label: 'BOOKY Work', desc: 'Fascicoli di lavoro' },
                { icon: <StarsIcon sx={{ fontSize: 18, color: '#FFD700' }} />, label: '3 ricerche al giorno gratis', desc: 'Poi con crediti senza abbonamento' },
              ].map(({ icon, label, desc }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    {icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{label}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>{desc}</Typography>
                  </Box>
                </Box>
              ))}

              <Button
                variant="contained"
                href="https://itfplus.it"
                target="_blank"
                endIcon={<ArrowIcon />}
                sx={{
                  mt: 1, bgcolor: '#A67C52', color: 'white',
                  fontWeight: 700, textTransform: 'none', borderRadius: 2,
                  '&:hover': { bgcolor: '#8B6542' }
                }}
              >
                Prova ITF Plus gratis
              </Button>
            </Box>
          </Box>
        </Box>

        <Typography
          variant="caption" color="text.disabled"
          sx={{ display: 'block', textAlign: 'center', mt: 4, pb: 2 }}
        >
          Documento generato da BOOKY Work · ITF Plus · itfplus.it
        </Typography>
      </Container>
    </Box>
  );
};

export default FascicoloPubblicoPage;
